import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { FloatingOverlay, FloatingPortal, useFloating } from '@floating-ui/react';
import { Input } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { TbCircleXFilled, TbSearch } from 'react-icons/tb';
import { RemoveScroll } from 'react-remove-scroll';
import { useLocation } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import InviteToWorkspaceModal from '@/shared/components/modals/GlobalModals/InviteToWorkspaceModal';
import CreateDashboardModal from '@/shared/components/modals/ResourceCreationModals/CreateDashboardModal';
import CreateFolderModal from '@/shared/components/modals/ResourceCreationModals/CreateFolderModal';
import CreateInvestmentModal from '@/shared/components/modals/ResourceCreationModals/CreateInvestmentModal';
import CreateListModal from '@/shared/components/modals/ResourceCreationModals/CreateListModal';
import CreatePipelineModal from '@/shared/components/modals/ResourceCreationModals/CreatePipelineModal';
import CreateTaskModal from '@/shared/components/modals/ResourceCreationModals/CreateTaskModal';
import CreateTeamModal from '@/shared/components/modals/ResourceCreationModals/CreateTeamModal';
import { SetStateMethod } from '@/types';

import { useGlobalCommandsRegistry } from '../../../../../hooks/useGlobalCommandsRegistry';
import Content from './layout/01-Content';
import { useCommandPaletteResourceContext } from './layout/01-Content/hooks/useCommandPaletteResourceContext';
import Footer from './layout/03-Footer';

type Command = {
    id: string;
    label: string;
    labelSuffix: ReactNode | string;
    group?: string;
    shortcut?: ReactNode | string;
    icon?: ReactNode;
    onSelect: () => void;
    context?: string; // e.g. 'workspace', 'project', 'report'
    metadata?: Record<string, any>; // used for previews
};

interface CommandPaletteProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const CommandPalette: FC<CommandPaletteProps> = ({ open, setOpen }): JSX.Element => {
    const [creationModalOpen, setCreationModalOpen] = useState(false);

    const [selectedKey, setSelectedKey] = useState('');

    // !TODO: Standardize to useMaps()
    const modalMap = {
        createFolder: <CreateFolderModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        createInvestment: <CreateInvestmentModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        createPipeline: <CreatePipelineModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        createTeam: <CreateTeamModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        inviteToWorkspace: <InviteToWorkspaceModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        // Relations
        createDashboard: <CreateDashboardModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        createList: <CreateListModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
        createTask: <CreateTaskModal open={creationModalOpen} setOpen={setCreationModalOpen} />,
    } as Record<string, ReactNode>;

    const location = useLocation();

    // Different Commands Based on View
    const [viewMode, setViewMode] = useState<'home' | 'investment'>('home');
    const [contextId, setContextId] = useState<string | null>(null);

    useEffect(() => {
        const match = location.pathname.match(/^\/investments\/(.+)$/);

        if (match) {
            setViewMode('investment');

            setContextId(match[1]);
        } else {
            setViewMode('home');

            setContextId(null);
        }
    }, [location.pathname]);

    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const globalCommands: Command[] | any = useGlobalCommandsRegistry({
        setOpen,
        setSelectedKey,
        setCreationModalOpen,
    });
    const resourceContextState = useCommandPaletteResourceContext({ open });

    // const detailCommands: Command[] | any = useDetailCommandsRegistry({
    //     setOpen,
    //     resource: 'investments',
    // });

    const { refs, floatingStyles } = useFloating({
        placement: 'top',
        middleware: [],
    });

    // Close command palette by clicking outside of it
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;

            if (target?.closest('.ant-modal-root, .ant-modal-wrap, .ant-modal-mask')) {
                return;
            }

            if (containerRef.current && target && !containerRef.current.contains(target)) {
                setOpen(false); // Close modal
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, setOpen]);

    const { token } = useAntDesignTheme();

    return (
        <>
            <FloatingPortal>
                <AnimatePresence>
                    {open && (
                        <FloatingOverlay
                            lockScroll
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                paddingTop: '10vh',
                                zIndex: token.zIndexPopupBase + 1000,
                            }}
                        >
                            <RemoveScroll>
                                <motion.div
                                    // ref={refs.setFloating}
                                    ref={(node: any) => {
                                        refs.setFloating(node);

                                        containerRef.current = node;
                                    }}
                                    style={{
                                        width: 16 * 52,
                                        background: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorder}`,
                                        borderRadius: token.borderRadius,
                                        boxShadow: token.boxShadow,
                                        overflow: 'hidden',
                                    }}
                                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                                    transition={{
                                        type: 'spring',
                                        damping: 24,
                                        stiffness: 180,
                                        duration: 0.125,
                                    }}
                                >
                                    {/* {viewMode !== 'home' && (
                                        <Nested
                                            open={open}
                                            setOpen={setOpen}
                                            viewMode={viewMode}
                                            setViewMode={setViewMode}
                                            contextId={contextId}
                                            setContextId={setContextId}
                                        />
                                    )} */}
                                    <div
                                        style={{
                                            padding: '0.5rem 0.25rem',
                                            borderBottom: `1px solid ${token.colorBorder}`,
                                        }}
                                    >
                                        <Input
                                            bordered={false}
                                            ref={inputRef as any}
                                            autoFocus
                                            prefix={
                                                <TbSearch
                                                    size='1rem'
                                                    style={{ marginRight: '0.5rem', color: token.colorTextQuaternary }}
                                                />
                                            }
                                            placeholder='Type A Command Or Search In Workspace...'
                                            value={query}
                                            onChange={(e) => {
                                                setQuery(e.target.value);

                                                setSelectedIndex(0);
                                            }}
                                            allowClear={{
                                                clearIcon: (
                                                    <TbCircleXFilled
                                                        size='1.125rem'
                                                        style={{ verticalAlign: '-0.3rem' }}
                                                    />
                                                ),
                                            }}
                                            style={{
                                                width: '100%',
                                                // border: `1px solid ${token.colorBorder}`,

                                                outline: 'none',
                                                boxShadow: 'none',
                                                paddingLeft: '0.5rem',
                                                paddingRight: '0.375rem',
                                            }}
                                        />
                                    </div>
                                    <Content
                                        globalCommands={globalCommands}
                                        resourceContextState={resourceContextState}
                                        selectedIndex={selectedIndex}
                                        setSelectedIndex={setSelectedIndex}
                                        query={query}
                                        setQuery={setQuery}
                                        open={open}
                                        setOpen={setOpen}
                                        inputRef={inputRef}
                                    />
                                    <Footer />
                                </motion.div>
                            </RemoveScroll>
                        </FloatingOverlay>
                    )}
                </AnimatePresence>
            </FloatingPortal>
            {modalMap[selectedKey]}
            {resourceContextState.resourceContextModals}
        </>
    );
};

export default CommandPalette;
